import type { ReactElement } from "react";
import { Link } from "react-router";

import { useApiData } from "../../api/use-api-data.js";
import { memberUrls } from "../../app/member-urls.js";
import { usePageMeta } from "../../app/use-page-meta.js";
import { loadFailureMessage } from "../../components/load-failure.js";
import { useRowLink } from "../../components/row-link.js";

import { roleBadge, type TenantRole } from "./tenant-role.js";

/** 契約 1 行（<c>GET /api/tenants</c>。サーバの TenantListItem と同じ欄）。中身は契約情報（tenant-detail-page）で出す。 */
export interface TenantRow extends TenantRole {
    readonly tenantId: number;
    readonly title: string;
    readonly company: string;
    readonly roleLabel: string;
    /** 一般の利用者（契約者でも管理者でもない人）には null（サーバが付けない）。 */
    readonly planDisplayName: string | null;
    /**
     * 自分のお名前の変更の画面。<b>ユーザ管理を開けない人（一般の利用者）にだけサーバが付ける</b>（ほかは null）。
     * 付いていれば、行を押したときに契約情報ではなくお名前の変更を直接開く。
     */
    readonly nameChangeUrl: string | null;
}

export interface TenantList {
    readonly tenants: readonly TenantRow[];
    readonly currentServiceId: string;
    /**
     * 請求書のメニュー（<c>/TenantInvoice/Menu</c>）。<b>請求書を見られる契約が 1 つも無い人には null</b>（サーバが決める）。
     * 一覧に無い契約（閲覧者に書かれているだけの契約）の請求書も、ここから開く。
     */
    readonly invoiceMenuUrl: string | null;
}

/** 行を押したときに開く画面。一般の利用者はお名前の変更、ほかは契約情報（サーバが nameChangeUrl で決める）。 */
const rowHref = (tenant: TenantRow): string => tenant.nameChangeUrl ?? memberUrls.tenantDetail(tenant.tenantId);

/** 行の操作（行を押したときの遷移と、サービスにログインの行き先）。 */
interface RowActions {
    readonly onRowClick: ReturnType<ReturnType<typeof useRowLink>>;
    readonly serviceUrl: string;
}

/**
 * 契約 1 行。<b>文言はサーバが作る</b>（役割・プラン）。
 * <b>行のどこを押しても開く</b>（契約情報、または一般の利用者はお名前の変更。2026-10-10 に利用者が決めた）。
 * キーボードと読み上げのために、システム名だけは本物のリンクにしておく（行の押し方は useRowLink）。
 * <b>行の右に「サービスにログイン」を置く</b>（全員。一般の利用者は契約情報を開かないため、ここが入口になる。2026-10-15 に利用者が決めた）。
 * 行き先は契約情報の画面のボタンと同じ。押したときは useRowLink がボタンに譲るので、行の遷移と重ならない。
 * 狭い画面では行を縦に積む（data-table-stack。欄の名前は data-label から出す）。
 */
function TenantTableRow({ tenant, actions }: {
    readonly tenant: TenantRow;
    readonly actions: RowActions;
}): ReactElement {
    const href = rowHref(tenant);
    return (
        <tr data-tenant-row="" data-row-href={href} onClick={actions.onRowClick}>
            <td data-label="システム名" className="font-semibold text-slate-900">
                <Link to={href} className="break-words hover:underline" data-tenant-title="">{tenant.title}</Link>
            </td>
            <td data-label="企業名" className="break-words">{tenant.company}</td>
            <td data-label="役割"><span className={roleBadge(tenant)}>{tenant.roleLabel}</span></td>
            <td data-label="プラン">{tenant.planDisplayName ?? "—"}</td>
            <td className="cell-actions">
                <a className="btn-primary btn-sm" href={actions.serviceUrl} data-service-login="">サービスにログイン</a>
            </td>
        </tr>
    );
}

function TenantTable({ list }: { readonly list: TenantList }): ReactElement {
    const rowLink = useRowLink();
    const serviceUrl = memberUrls.service(list.currentServiceId);
    return (
        <div className="table-card">
            <div className="table-scroll">
                <table className="data-table data-table-stack">
                    <thead>
                        <tr>
                            <th scope="col">システム名</th>
                            <th scope="col">企業名</th>
                            <th scope="col">役割</th>
                            <th scope="col">プラン</th>
                            <th scope="col"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {list.tenants.map((tenant) => (
                            <TenantTableRow key={tenant.tenantId} tenant={tenant} actions={{ onRowClick: rowLink(rowHref(tenant)), serviceUrl }} />
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

/** 契約一覧（ログインした後の着地点）。1 契約 1 行の表。 */
export function TenantListPage(): ReactElement {
    usePageMeta("契約一覧");
    const data = useApiData<TenantList>("/api/tenants");

    return (
        <div className="page max-w-6xl">
            <div className="page-header">
                <div>
                    <h1 className="page-title">契約一覧</h1>
                    <p className="page-subtitle">ご利用中の環境の一覧です。</p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    {/* 請求書のメニューの入口。出すかどうかはサーバが決める（見られる契約が無ければ null） */}
                    {data.state === "ready" && typeof data.value.invoiceMenuUrl === "string"
                        ? <Link to={data.value.invoiceMenuUrl} className="btn-secondary" data-invoice-menu="">請求書</Link>
                        : null}
                    <a className="btn-primary" href={memberUrls.newTenant}>新規申し込み</a>
                </div>
            </div>

            {data.state === "loading" ? <p className="text-slate-500">読み込んでいます…</p> : null}
            {data.state === "failed"
                ? <div className="alert-danger" role="alert">{loadFailureMessage("契約の一覧", data)}</div>
                : null}
            {data.state === "ready" && data.value.tenants.length === 0
                ? (
                    <div className="empty-state">
                        <p className="mb-5 text-slate-500">ご契約中の環境はありません。</p>
                        <div><a className="btn-primary" href={memberUrls.newTenant}>新規申し込み</a></div>
                    </div>
                )
                : null}
            {data.state === "ready" && data.value.tenants.length > 0 ? <TenantTable list={data.value} /> : null}
        </div>
    );
}
